import { useState, useEffect } from "react";
import {
  listPromoCodes,
  createPromoCode,
  deactivatePromoCode,
  type AdminPromoCode,
  type CreatePromoCodeRequest,
} from "../../services/adminService";

export function PromoCodePanel() {
  const [codes, setCodes] = useState<AdminPromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  // Form state
  const [formCode, setFormCode] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formDiscount, setFormDiscount] = useState("");
  const [formStartsAt, setFormStartsAt] = useState("");
  const [formEndsAt, setFormEndsAt] = useState("");
  const [formMaxUses, setFormMaxUses] = useState("");
  const [formIsPublic, setFormIsPublic] = useState(true);

  const loadCodes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await listPromoCodes();
      setCodes(res.promo_codes || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load promo codes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const resetForm = () => {
    setFormCode("");
    setFormDescription("");
    setFormDiscount("");
    setFormStartsAt("");
    setFormEndsAt("");
    setFormMaxUses("");
    setFormIsPublic(true);
    setShowForm(false);
  };

  const handleCreate = async () => {
    if (!formCode || !formDiscount || !formStartsAt || !formEndsAt) {
      setError("Code, discount %, start date, and end date are required");
      return;
    }

    const discount = parseFloat(formDiscount);
    if (isNaN(discount) || discount <= 0 || discount > 100) {
      setError("Discount must be between 1 and 100");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const req: CreatePromoCodeRequest = {
        code: formCode.toUpperCase(),
        description: formDescription,
        discount_percent: discount,
        starts_at: new Date(formStartsAt).toISOString(),
        ends_at: new Date(formEndsAt).toISOString(),
        is_public: formIsPublic,
      };

      if (formMaxUses) {
        const maxUses = parseInt(formMaxUses);
        if (!isNaN(maxUses) && maxUses > 0) {
          req.max_uses = maxUses;
        }
      }

      await createPromoCode(req);
      setSuccess("Promo code created successfully");
      setTimeout(() => setSuccess(null), 3000);
      resetForm();
      loadCodes();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create promo code");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      setDeactivatingId(id);
      setError(null);
      await deactivatePromoCode(id);
      setSuccess("Promo code deactivated");
      setTimeout(() => setSuccess(null), 3000);
      loadCodes();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to deactivate promo code");
    } finally {
      setDeactivatingId(null);
    }
  };

  const getStatusBadge = (code: AdminPromoCode) => {
    if (!code.is_active) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-surface-700/50 text-surface-400 border border-surface-600/50">
          Deactivated
        </span>
      );
    }
    if (code.is_expired) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">
          Expired
        </span>
      );
    }
    const now = new Date();
    if (new Date(code.starts_at) > now) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
          Scheduled
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30">
        Active
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <div className="animate-spin w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full" />
          </div>
          <span className="text-surface-400">Loading promo codes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium text-surface-100">Promo Codes</div>
            <div className="text-xs text-surface-400">
              Create and manage promotional discount codes
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-all"
        >
          {showForm ? "Cancel" : "+ New Code"}
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
          {success}
        </div>
      )}

      {/* Create Form */}
      {showForm && (
        <div className="mb-4 p-4 rounded-lg bg-surface-900/50 border border-surface-700/50 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1">
                Code
              </label>
              <input
                type="text"
                value={formCode}
                onChange={(e) => setFormCode(e.target.value.toUpperCase())}
                placeholder="SUMMER25"
                className="input-field w-full text-sm"
                maxLength={30}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1">
                Discount %
              </label>
              <input
                type="number"
                value={formDiscount}
                onChange={(e) => setFormDiscount(e.target.value)}
                placeholder="10"
                min="1"
                max="100"
                className="input-field w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Summer 2025 promotion - 25% off first month"
              className="input-field w-full text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={formStartsAt}
                onChange={(e) => setFormStartsAt(e.target.value)}
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1">
                End Date
              </label>
              <input
                type="datetime-local"
                value={formEndsAt}
                onChange={(e) => setFormEndsAt(e.target.value)}
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1">
                Max Uses
              </label>
              <input
                type="number"
                value={formMaxUses}
                onChange={(e) => setFormMaxUses(e.target.value)}
                placeholder="Unlimited"
                min="1"
                className="input-field w-full text-sm"
              />
              <p className="text-xs text-surface-500 mt-0.5">Leave empty = unlimited</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="formIsPublic"
              checked={formIsPublic}
              onChange={(e) => setFormIsPublic(e.target.checked)}
              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-amber-500 focus:ring-amber-500/30"
            />
            <label htmlFor="formIsPublic" className="text-xs text-surface-400">
              Publicly visible on subscription page
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={resetForm}
              className="px-3 py-1.5 rounded-lg text-sm bg-surface-700 text-surface-300 hover:bg-surface-600 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={isSubmitting}
              className="px-4 py-1.5 rounded-lg text-sm bg-amber-500 text-white hover:bg-amber-600 transition-all disabled:opacity-50"
            >
              {isSubmitting ? "Creating..." : "Create Promo Code"}
            </button>
          </div>
        </div>
      )}

      {/* Codes List */}
      {codes.length === 0 ? (
        <div className="text-center py-4 text-surface-500 text-sm">
          No promo codes created yet
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
          {codes.map((code) => (
            <div
              key={code.id}
              className="flex items-center justify-between p-3 rounded-lg bg-surface-900/30 border border-surface-700/30"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-medium text-surface-100">
                    {code.code}
                  </span>
                  {getStatusBadge(code)}
                  {!code.is_public && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/15 text-purple-400 border border-purple-500/30">
                      Hidden
                    </span>
                  )}
                  <span className="text-xs text-amber-400 font-medium">
                    {code.discount_percent}% off
                  </span>
                </div>
                <div className="text-xs text-surface-400 truncate">
                  {code.description || "No description"}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-surface-500">
                  <span>{formatDate(code.starts_at)} - {formatDate(code.ends_at)}</span>
                  <span>
                    Used: {code.times_used}{code.max_uses > 0 ? `/${code.max_uses}` : ""}
                  </span>
                </div>
              </div>
              {code.is_active && !code.is_expired && (
                <button
                  onClick={() => handleDeactivate(code.id)}
                  disabled={deactivatingId === code.id}
                  className="ml-3 px-2.5 py-1 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all disabled:opacity-50"
                >
                  {deactivatingId === code.id ? "..." : "Deactivate"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
